import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getGraphQLClient,
  GET_BOOK_GITHUB_SYNC,
  CONFIGURE_GITHUB_SYNC,
  PUSH_BOOK_TO_GITHUB,
  RESOLVE_SCENE_CONFLICT,
  RESOLVE_WRITING_STYLE_CONFLICT,
  FORK_BOOK_FROM_GITHUB,
  GET_GITHUB_CONNECTION,
  START_GITHUB_OAUTH,
  DISCONNECT_GITHUB,
  GET_GITHUB_REPOS,
} from '../lib/graphql';
import { captureException } from '../lib/sentry';
import { Button, Spinner } from '../components/common';
import RepoPicker, { RepoPickerOption } from '../components/github/RepoPicker';

type GithubRepoOption = RepoPickerOption;

type GithubConnection = {
  id: string;
  githubConnected: boolean;
  githubLogin: string | null;
};

type ConflictScene = {
  id: string;
  title: string;
  body: string | null;
  conflictRepoText: string | null;
};

type BookSync = {
  id: string;
  title: string | null;
  githubRepo: string | null;
  githubSyncConfigured: boolean;
  lastSyncedCommitSha: string | null;
  lastSyncedAt: string | null;
  anySyncConflicts: boolean;
  writingStyle: string | null;
  writingStyleConflicted: boolean;
  writingStyleConflictRepoText: string | null;
  conflictedScenes: ConflictScene[];
};

type PushStatus = {
  committed: boolean;
  noChanges: boolean;
  commitSha: string | null;
  branch: string | null;
};

type Choice = 'mine' | 'repo' | 'merge';

export default function BookSyncPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { bookId } = useParams<{ bookId: string }>();
  const [book, setBook] = useState<BookSync | null>(null);
  const [loading, setLoading] = useState(true);
  const [connection, setConnection] = useState<GithubConnection | null>(null);
  const [repos, setRepos] = useState<GithubRepoOption[] | null>(null);
  const [reposLoading, setReposLoading] = useState(false);
  const [repoSelection, setRepoSelection] = useState('');
  const [savingConfig, setSavingConfig] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pushResult, setPushResult] = useState<PushStatus | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [forking, setForking] = useState(false);
  // Resolution-in-flight + draft merge text, keyed by conflict id
  // ("writing_style" for the book-level one, scene id otherwise).
  const [resolving, setResolving] = useState<Record<string, boolean>>({});
  const [mergeText, setMergeText] = useState<Record<string, string>>({});
  const [mergeOpen, setMergeOpen] = useState<Record<string, boolean>>({});

  const loadBook = async () => {
    if (!bookId) return null;
    const client = getGraphQLClient();
    const data = await client.request<{ book: BookSync }>(GET_BOOK_GITHUB_SYNC, { id: bookId });
    setBook(data.book);
    return data.book;
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!bookId) return;
      try {
        const client = getGraphQLClient();
        const [bookData, meData] = await Promise.all([
          client.request<{ book: BookSync }>(GET_BOOK_GITHUB_SYNC, { id: bookId }),
          client.request<{ me: GithubConnection | null }>(GET_GITHUB_CONNECTION),
        ]);
        if (cancelled) return;
        setBook(bookData.book);
        setRepoSelection(bookData.book.githubRepo ?? '');
        setConnection(meData.me);
      } catch (err) {
        captureException(err);
        if (!cancelled) setError(t('common.error'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [bookId, t]);

  // Lazy-load the repo list: only fetch when the picker is actually shown.
  const needsPicker = !!connection?.githubConnected && (!book?.githubRepo || pickerOpen);
  useEffect(() => {
    if (!needsPicker || repos !== null) return;
    let cancelled = false;
    setReposLoading(true);
    getGraphQLClient()
      .request<{ githubRepos: GithubRepoOption[] }>(GET_GITHUB_REPOS)
      .then((data) => {
        if (!cancelled) setRepos(data.githubRepos);
      })
      .catch((err) => {
        captureException(err);
      })
      .finally(() => {
        if (!cancelled) setReposLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [needsPicker, repos]);

  const handleConnect = async () => {
    setConnecting(true);
    setError(null);
    try {
      const client = getGraphQLClient();
      const data = await client.request<{
        startGithubOauth: { authorizeUrl: string | null; errors: string[] };
      }>(START_GITHUB_OAUTH, { returnTo: location.pathname });
      if (data.startGithubOauth.errors.length > 0) {
        setError(data.startGithubOauth.errors.join('; '));
        setConnecting(false);
        return;
      }
      if (data.startGithubOauth.authorizeUrl) {
        window.location.href = data.startGithubOauth.authorizeUrl;
        return;
      }
    } catch (err) {
      captureException(err);
      setError(t('common.error'));
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (
      !window.confirm(
        t(
          'githubSync.disconnectConfirm',
          'Disconnect your GitHub account from BookTypist? Books that point at GitHub repos will stop syncing until you reconnect.'
        )
      )
    ) {
      return;
    }
    setDisconnecting(true);
    setError(null);
    try {
      const client = getGraphQLClient();
      const data = await client.request<{
        disconnectGithub: { user: GithubConnection | null; errors: string[] };
      }>(DISCONNECT_GITHUB);
      if (data.disconnectGithub.errors.length > 0) {
        setError(data.disconnectGithub.errors.join('; '));
      } else if (data.disconnectGithub.user) {
        setConnection(data.disconnectGithub.user);
        setBook((prev) => (prev ? { ...prev, githubSyncConfigured: false } : prev));
      }
    } catch (err) {
      captureException(err);
      setError(t('common.error'));
    } finally {
      setDisconnecting(false);
    }
  };

  const handleSaveConfig = async () => {
    if (!bookId || !repoSelection.trim()) return;
    setSavingConfig(true);
    setError(null);
    try {
      const client = getGraphQLClient();
      const data = await client.request<{
        configureGithubSync: { book: BookSync | null; errors: string[] };
      }>(CONFIGURE_GITHUB_SYNC, {
        bookId,
        githubRepo: repoSelection.trim(),
      });
      if (data.configureGithubSync.errors.length > 0) {
        setError(data.configureGithubSync.errors.join('; '));
      } else if (data.configureGithubSync.book) {
        setBook((prev) =>
          prev ? { ...prev, ...data.configureGithubSync.book! } : data.configureGithubSync.book
        );
        setPickerOpen(false);
      }
    } catch (err) {
      captureException(err);
      setError(t('common.error'));
    } finally {
      setSavingConfig(false);
    }
  };

  // Sync = pull + reconcile + commit in one call. If a conflict is open the
  // commit is held back; we reload to surface the conflicts below for resolution.
  const handleSync = async () => {
    if (!bookId) return;
    setSyncing(true);
    setError(null);
    setPushResult(null);
    try {
      const client = getGraphQLClient();
      const data = await client.request<{
        pushBookToGithub: { status: PushStatus | null; conflicts: boolean; errors: string[] };
      }>(PUSH_BOOK_TO_GITHUB, { bookId, message: null });
      if (data.pushBookToGithub.errors.length > 0) {
        setError(data.pushBookToGithub.errors.join('; '));
      }
      if (data.pushBookToGithub.status) setPushResult(data.pushBookToGithub.status);
      await loadBook();
    } catch (err) {
      captureException(err);
      setError(t('common.error'));
    } finally {
      setSyncing(false);
    }
  };

  const resolveScene = async (sceneId: string, choice: Choice) => {
    setResolving((prev) => ({ ...prev, [sceneId]: true }));
    setError(null);
    try {
      const client = getGraphQLClient();
      const data = await client.request<{ resolveSceneConflict: { errors: string[] } }>(
        RESOLVE_SCENE_CONFLICT,
        { sceneId, choice, mergedText: choice === 'merge' ? (mergeText[sceneId] ?? '') : null }
      );
      if (data.resolveSceneConflict.errors.length > 0) {
        setError(data.resolveSceneConflict.errors.join('; '));
      }
      await loadBook();
    } catch (err) {
      captureException(err);
      setError(t('common.error'));
    } finally {
      setResolving((prev) => ({ ...prev, [sceneId]: false }));
    }
  };

  const resolveWritingStyle = async (choice: Choice) => {
    if (!bookId) return;
    setResolving((prev) => ({ ...prev, writing_style: true }));
    setError(null);
    try {
      const client = getGraphQLClient();
      const data = await client.request<{ resolveWritingStyleConflict: { errors: string[] } }>(
        RESOLVE_WRITING_STYLE_CONFLICT,
        { bookId, choice, mergedText: choice === 'merge' ? (mergeText.writing_style ?? '') : null }
      );
      if (data.resolveWritingStyleConflict.errors.length > 0) {
        setError(data.resolveWritingStyleConflict.errors.join('; '));
      }
      await loadBook();
    } catch (err) {
      captureException(err);
      setError(t('common.error'));
    } finally {
      setResolving((prev) => ({ ...prev, writing_style: false }));
    }
  };

  const handleFork = async () => {
    if (!bookId) return;
    if (
      !window.confirm(
        t(
          'githubSync.forkConfirm',
          'Create a new book populated from the current GitHub state? This leaves the current book untouched.'
        )
      )
    ) {
      return;
    }
    setForking(true);
    setError(null);
    try {
      const client = getGraphQLClient();
      const data = await client.request<{
        forkBookFromGithub: { book: { id: string; title: string | null } | null; errors: string[] };
      }>(FORK_BOOK_FROM_GITHUB, { bookId });
      if (data.forkBookFromGithub.errors.length > 0) {
        setError(data.forkBookFromGithub.errors.join('; '));
      } else if (data.forkBookFromGithub.book) {
        navigate(`/books/${data.forkBookFromGithub.book.id}/refine`);
      }
    } catch (err) {
      captureException(err);
      setError(t('common.error'));
    } finally {
      setForking(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[40vh] gap-3">
        <Spinner />
      </div>
    );
  }

  if (!book) {
    return (
      <div className="max-w-2xl mx-auto py-12 px-4">
        <p className="text-red-600">{t('common.error')}</p>
      </div>
    );
  }

  const lastSyncedDisplay = book.lastSyncedAt ? new Date(book.lastSyncedAt).toLocaleString() : null;

  // A reusable mine/repo/merge resolution card.
  const renderConflict = (
    id: string,
    title: string,
    mineLabel: string,
    mine: string | null,
    repo: string | null,
    onResolve: (choice: Choice) => void
  ) => (
    <div key={id} className="rounded-lg border border-red-200 dark:border-red-900 p-4 space-y-3">
      <div className="flex items-baseline justify-between gap-3">
        <div className="font-medium text-gray-900 dark:text-gray-100">{title}</div>
        <span className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-800">
          {t('githubSync.conflict.badge', 'conflict')}
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
        {(
          [
            ['mine', mineLabel, mine],
            ['repo', t('githubSync.conflict.remote', 'Repo (git)'), repo],
          ] as const
        ).map(([k, label, value]) => (
          <div key={k} className="space-y-1">
            <div className="font-medium text-gray-500 uppercase tracking-wide">{label}</div>
            <pre className="whitespace-pre-wrap max-h-40 overflow-auto bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded p-2 text-gray-800 dark:text-gray-200">
              {value ?? ''}
            </pre>
          </div>
        ))}
      </div>
      <div className="flex gap-2 flex-wrap items-center">
        <Button
          size="sm"
          variant="secondary"
          isLoading={resolving[id]}
          onClick={() => onResolve('mine')}
        >
          {t('githubSync.conflict.keepMine', 'Keep mine')}
        </Button>
        <Button
          size="sm"
          variant="secondary"
          isLoading={resolving[id]}
          onClick={() => onResolve('repo')}
        >
          {t('githubSync.conflict.useRepo', 'Use repo')}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            setMergeOpen((prev) => ({ ...prev, [id]: !prev[id] }));
            if (mergeText[id] === undefined)
              setMergeText((prev) => ({ ...prev, [id]: repo ?? '' }));
          }}
        >
          {t('githubSync.conflict.merge', 'Merge…')}
        </Button>
      </div>
      {mergeOpen[id] && (
        <div className="space-y-2">
          <textarea
            value={mergeText[id] ?? ''}
            onChange={(e) => setMergeText((prev) => ({ ...prev, [id]: e.target.value }))}
            className="w-full min-h-[8rem] rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 p-2 text-sm font-mono"
          />
          <Button size="sm" isLoading={resolving[id]} onClick={() => onResolve('merge')}>
            {t('githubSync.conflict.saveMerge', 'Save merged version')}
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto py-10 px-4 space-y-8">
      <div>
        <button
          onClick={() => navigate(`/books/${bookId}/refine`)}
          className="text-sm text-gray-500 hover:text-gray-700 mb-3"
        >
          ← {t('common.back', 'Back')}
        </button>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          {t('githubSync.title', 'GitHub Sync')}
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {t(
            'githubSync.subtitle',
            'Push this book to a GitHub repository as Markdown, and pull changes back. Connect your GitHub account once, then pick a repo per book.'
          )}
        </p>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <section className="space-y-4 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          {t('githubSync.connection', 'GitHub account')}
        </h2>

        {!connection?.githubConnected && (
          <div className="space-y-3">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t(
                'githubSync.connectAccountDescription',
                'Connect your GitHub account once. After that, you can sync any of your books to any repo you have access to — no tokens to manage.'
              )}
            </p>
            <Button onClick={handleConnect} isLoading={connecting} variant="primary">
              {t('githubSync.connectGithub', 'Connect GitHub')}
            </Button>
          </div>
        )}

        {connection?.githubConnected && (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-3 text-sm">
              <span className="text-gray-700 dark:text-gray-300">
                ✓ {t('githubSync.connectedAs', 'Connected as')}{' '}
                <span className="font-medium">{connection.githubLogin ?? '—'}</span>
              </span>
              <Button
                onClick={handleDisconnect}
                isLoading={disconnecting}
                variant="ghost"
                size="sm"
              >
                {t('githubSync.disconnect', 'Disconnect')}
              </Button>
            </div>

            {book.githubRepo && !pickerOpen && (
              <div className="flex items-center justify-between gap-3 text-sm">
                <div className="min-w-0">
                  <div className="text-gray-500 text-xs uppercase tracking-wide">
                    {t('githubSync.repoLabel', 'Repository for this book')}
                  </div>
                  <a
                    href={`https://github.com/${book.githubRepo}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-mono text-gray-900 dark:text-gray-100 hover:underline truncate inline-block max-w-full"
                  >
                    {book.githubRepo}
                  </a>
                </div>
                <Button
                  onClick={() => {
                    setRepoSelection(book.githubRepo ?? '');
                    setPickerOpen(true);
                  }}
                  variant="ghost"
                  size="sm"
                >
                  {t('githubSync.changeRepo', 'Change…')}
                </Button>
              </div>
            )}

            {(!book.githubRepo || pickerOpen) && (
              <div className="space-y-2">
                <div className="flex items-baseline justify-between gap-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('githubSync.repoLabel', 'Repository for this book')}
                  </label>
                  {repoSelection && (
                    <span className="text-xs font-mono text-gray-500 truncate max-w-[60%]">
                      {repoSelection}
                    </span>
                  )}
                </div>
                {reposLoading && !repos && (
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Spinner /> {t('githubSync.loadingRepos', 'Loading your repositories…')}
                  </div>
                )}
                {repos && (
                  <RepoPicker
                    repos={repos}
                    value={repoSelection}
                    onChange={setRepoSelection}
                    viewerLogin={connection.githubLogin}
                  />
                )}
                <p className="text-xs text-gray-500">
                  {t(
                    'githubSync.repoHelper',
                    'Pick the repository this book should sync to. You can change it later.'
                  )}
                </p>
                <div className="flex items-center gap-3 pt-1">
                  <Button
                    onClick={handleSaveConfig}
                    isLoading={savingConfig}
                    disabled={!repoSelection.trim() || repoSelection === (book.githubRepo ?? '')}
                  >
                    {book.githubSyncConfigured
                      ? t('githubSync.updateConnection', 'Update repository')
                      : t('githubSync.connect', 'Use this repository')}
                  </Button>
                  {book.githubRepo && (
                    <Button onClick={() => setPickerOpen(false)} variant="ghost" size="sm">
                      {t('common.cancel', 'Cancel')}
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      <section className="space-y-4 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          {t('githubSync.sync', 'Sync')}
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {t(
            'githubSync.syncDescription',
            'Sync pulls repo edits, reconciles them, and commits your manuscript back — all in one step. Edits made on only one side flow automatically; edits made on both sides are flagged below as conflicts to resolve.'
          )}
        </p>
        {lastSyncedDisplay && (
          <p className="text-xs text-gray-500">
            {t('githubSync.lastSynced', 'Last synced')}: {lastSyncedDisplay}
            {book.lastSyncedCommitSha && (
              <span className="ml-2 font-mono">({book.lastSyncedCommitSha.slice(0, 7)})</span>
            )}
          </p>
        )}

        {pushResult && (
          <div className="rounded-lg bg-gray-50 dark:bg-gray-800 px-4 py-3 text-sm">
            {pushResult.noChanges ? (
              <span className="text-gray-700 dark:text-gray-300">
                {t('githubSync.noChanges', 'No changes to push — repo is already up to date.')}
              </span>
            ) : pushResult.committed ? (
              <span className="text-green-700 dark:text-green-400">
                ✓ {t('githubSync.pushed', 'Pushed')}{' '}
                {pushResult.commitSha && (
                  <span className="font-mono">({pushResult.commitSha.slice(0, 7)})</span>
                )}{' '}
                {pushResult.branch && <span>→ {pushResult.branch}</span>}
              </span>
            ) : null}
          </div>
        )}

        <div className="flex items-center gap-3 flex-wrap">
          <Button
            onClick={handleSync}
            isLoading={syncing}
            disabled={!book.githubSyncConfigured}
            variant="accent"
          >
            {t('githubSync.syncButton', 'Sync with GitHub')}
          </Button>
          <Button
            onClick={handleFork}
            isLoading={forking}
            disabled={!book.githubSyncConfigured}
            variant="ghost"
            size="sm"
          >
            {t('githubSync.fork', 'Fork to new book…')}
          </Button>
        </div>

        {book.anySyncConflicts && (
          <div className="space-y-3 pt-2">
            <h3 className="text-sm font-semibold text-red-700 dark:text-red-400">
              {t('githubSync.conflictsHeading', 'Conflicts to resolve before pushing')}
            </h3>
            {book.writingStyleConflicted &&
              renderConflict(
                'writing_style',
                t('githubSync.writingStyle', 'Writing style'),
                t('githubSync.conflict.local', 'Mine (web)'),
                book.writingStyle,
                book.writingStyleConflictRepoText,
                resolveWritingStyle
              )}
            {book.conflictedScenes.map((scene) =>
              renderConflict(
                scene.id,
                scene.title,
                t('githubSync.conflict.local', 'Mine (web)'),
                scene.body,
                scene.conflictRepoText,
                (choice) => resolveScene(scene.id, choice)
              )
            )}
          </div>
        )}
      </section>
    </div>
  );
}
